/**
 * API endpoint constants
 */

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    VALIDATE: '/v1/auth/validate',
    ME: '/v1/auth/me',
    LOGOUT: '/v1/auth/logout',
  },

  // Leave Management
  LEAVE: {
    BALANCES: '/v1/leave/balances',
    REQUESTS: '/v1/leave/requests',
    REQUEST: (id: string) => `/v1/leave/requests/${id}`,
    APPROVE: (id: string) => `/v1/leave/requests/${id}/approve`,
    REJECT: (id: string) => `/v1/leave/requests/${id}/reject`,
    TYPES: '/v1/leave/types',
    HOLIDAYS: '/v1/leave/holidays',
    CALENDAR: '/v1/leave/calendar',
    // Admin endpoints
    ADMIN_TYPES: '/v1/leave/admin/types',
    ADMIN_TYPE: (id: string) => `/v1/leave/admin/types/${id}`,
    ADMIN_HOLIDAYS: '/v1/leave/admin/holidays',
    ADMIN_HOLIDAY: (id: string) => `/v1/leave/admin/holidays/${id}`,
    ADMIN_USER_REQUESTS: (userId: string) => `/v1/leave/admin/users/${userId}/requests`,
    ADMIN_REQUEST: (id: string) => `/v1/leave/admin/requests/${id}`,
    // Reports
    REPORT_COMPREHENSIVE: '/v1/leave/reports/comprehensive',
    REPORT_EMPLOYEES: '/v1/leave/reports/employees',
    REPORT_DEPARTMENTS: '/v1/leave/reports/departments',
    REPORT_REQUESTS: '/v1/leave/reports/requests',
  },

  // Learning Management System
  LMS: {
    COURSES: '/v1/lms/courses',
    COURSE: (id: string) => `/v1/lms/courses/${id}`,
    ENROLL: (id: string) => `/v1/lms/courses/${id}/enroll`,
    ENROLLMENTS: '/v1/lms/enrollments',
    ENROLLMENT: (id: string) => `/v1/lms/enrollments/${id}`,
    PROGRESS: (id: string) => `/v1/lms/enrollments/${id}/progress`,
    CERTIFICATES: '/v1/lms/certificates',
  },

  // Reimbursement
  REIMBURSEMENT: {
    CLAIMS: '/v1/reimbursement/claims',
    CLAIM: (id: string) => `/v1/reimbursement/claims/${id}`,
    CLAIM_ITEMS: (id: string) => `/v1/reimbursement/claims/${id}/items`,
    CLAIM_ITEM: (claimId: string, itemId: string) => `/v1/reimbursement/claims/${claimId}/items/${itemId}`,
    SUBMIT: (id: string) => `/v1/reimbursement/claims/${id}/submit`,
    CANCEL: (id: string) => `/v1/reimbursement/claims/${id}/cancel`,
    APPROVE: (id: string) => `/v1/reimbursement/claims/${id}/approve`,
    REJECT: (id: string) => `/v1/reimbursement/claims/${id}/reject`,
    MARK_PAID: (id: string) => `/v1/reimbursement/claims/${id}/mark-paid`,
    CATEGORIES: '/v1/reimbursement/categories',
    POLICIES: '/v1/reimbursement/policies',
    PENDING_APPROVALS: '/v1/reimbursement/approvals/pending',
    PAYMENTS_READY: '/v1/reimbursement/payments/ready',
    PAYMENTS_HISTORY: '/v1/reimbursement/payments/history',
    REPORT_SUMMARY: '/v1/reimbursement/reports/summary',
    REPORT_COMPREHENSIVE: '/v1/reimbursement/reports/comprehensive',
    REPORT_EMPLOYEES: '/v1/reimbursement/reports/employees',
    REPORT_DEPARTMENTS: '/v1/reimbursement/reports/departments',
    REPORT_CLAIMS: '/v1/reimbursement/reports/claims',
    // Receipt endpoints
    ITEM_RECEIPTS: (claimId: string, itemId: string) => `/v1/reimbursement/claims/${claimId}/items/${itemId}/receipts`,
    ITEM_RECEIPT: (claimId: string, itemId: string, receiptId: string) => `/v1/reimbursement/claims/${claimId}/items/${itemId}/receipts/${receiptId}`,
    RECEIPT_DOWNLOAD: (receiptId: string) => `/v1/reimbursement/receipts/${receiptId}/download`,
    // Admin endpoints
    ADMIN_POLICY_CREATE: '/v1/reimbursement/admin/policies',
    ADMIN_POLICY: (id: string) => `/v1/reimbursement/admin/policies/${id}`,
    // Approval Thresholds
    ADMIN_APPROVAL_THRESHOLDS: '/v1/reimbursement/admin/approval-thresholds',
    ADMIN_APPROVAL_THRESHOLD: (id: string) => `/v1/reimbursement/admin/approval-thresholds/${id}`,
  },

  // Learning & Development
  LEARNING: {
    // Categories
    CATEGORIES: '/v1/learning/categories',
    CATEGORY: (id: string) => `/v1/learning/categories/${id}`,
    // Trainings
    TRAININGS: '/v1/learning/trainings',
    TRAINING: (id: string) => `/v1/learning/trainings/${id}`,
    TRAINING_SIMILAR: '/v1/learning/trainings/similar',
    TRAINING_STATS: (id: string) => `/v1/learning/trainings/${id}/stats`,
    TRAINING_REVIEWS: (id: string) => `/v1/learning/trainings/${id}/reviews`,
    TRAINING_DOCUMENTS: (id: string) => `/v1/learning/trainings/${id}/documents`,
    DOCUMENT: (id: string) => `/v1/learning/documents/${id}`,
    // Employee Training Records
    MY_RECORDS: '/v1/learning/my-records',
    RECORDS: '/v1/learning/records',
    RECORDS_ADMIN: '/v1/learning/records/admin',
    RECORD: (id: string) => `/v1/learning/records/${id}`,
    RECORD_VERIFY: (id: string) => `/v1/learning/records/${id}/verify`,
    RECORD_CERTIFICATE: (id: string) => `/v1/learning/records/${id}/certificate`,
    RECORD_CERTIFICATES: (id: string) => `/v1/learning/records/${id}/certificates`,
    CERTIFICATE: (id: string) => `/v1/learning/certificates/${id}`,
    // Training Requirements
    REQUIREMENTS: '/v1/learning/requirements',
    REQUIREMENT: (id: string) => `/v1/learning/requirements/${id}`,
    REQUIREMENTS_BULK: '/v1/learning/requirements/bulk',
    // Reports
    REPORT_COMPREHENSIVE: '/v1/learning/reports/comprehensive',
    REPORT_EMPLOYEES: '/v1/learning/reports/employees',
    REPORT_DEPARTMENTS: '/v1/learning/reports/departments',
    REPORT_RECORDS: '/v1/learning/reports/records',
    REPORT_CERTIFICATION_STATUS: '/v1/learning/reports/certification-status',
    REPORT_EMPLOYEES_OVERVIEW: '/v1/learning/reports/employees-overview',
  },

  // Common
  APPROVALS: {
    PENDING: '/v1/approvals/pending',
  },

  NOTIFICATIONS: {
    LIST: '/v1/notifications',
    MARK_READ: (id: string) => `/v1/notifications/${id}/mark-read`,
  },

  FILES: {
    DOWNLOAD: (id: string) => `/v1/files/${id}/download`,
  },

  // Lunch Expense
  LUNCH_EXPENSE: {
    // Admin endpoints
    ADMIN_EXPENSES: '/v1/lunch-expense/admin/expenses',
    ADMIN_EXPENSE: (id: string) => `/v1/lunch-expense/admin/expenses/${id}`,
    // Employee endpoints (read-only)
    EXPENSES: '/v1/lunch-expense/expenses',
    EXPENSE: (id: string) => `/v1/lunch-expense/expenses/${id}`,
    SUMMARY: '/v1/lunch-expense/expenses/summary',
  },

  // Penilaian Individu (360-degree assessment)
  PENILAIAN: {
    SS_INDICATORS: '/v1/penilaian/master/ss-indicators',
    SS_INDICATOR: (id: string) => `/v1/penilaian/master/ss-indicators/${id}`,
    SS_INDICATOR_QUESTIONS: (id: string) => `/v1/penilaian/master/ss-indicators/${id}/questions`,
    SS_QUESTION: (id: string) => `/v1/penilaian/master/ss-questions/${id}`,
    AKHLAK_VALUES: '/v1/penilaian/master/akhlak-values',
    AKHLAK_VALUE: (id: string) => `/v1/penilaian/master/akhlak-values/${id}`,
    AKHLAK_VALUE_BEHAVIORS: (id: string) => `/v1/penilaian/master/akhlak-values/${id}/behaviors`,
    AKHLAK_BEHAVIOR: (id: string) => `/v1/penilaian/master/akhlak-behaviors/${id}`,
    PERIODS: '/v1/penilaian/periods',
    PERIOD: (id: string) => `/v1/penilaian/periods/${id}`,
    PERIOD_KPIS: (periodId: string) => `/v1/penilaian/periods/${periodId}/kpis`,
    PERIOD_ASSIGNMENTS: (periodId: string) => `/v1/penilaian/periods/${periodId}/assignments`,
    MY_ASSIGNMENTS: (periodId: string) => `/v1/penilaian/periods/${periodId}/my-assignments`,
    BULK_ASSIGN: (periodId: string) => `/v1/penilaian/periods/${periodId}/assignments/bulk`,
    AUTO_ASSIGN: (periodId: string, division: string) =>
      `/v1/penilaian/periods/${periodId}/assignments/auto-assign/${encodeURIComponent(division)}`,
    AUTO_ASSIGN_ATASAN: (periodId: string) =>
      `/v1/penilaian/periods/${periodId}/assignments/auto-assign-atasan`,
    AUTO_ASSIGN_BAWAHAN: (periodId: string) =>
      `/v1/penilaian/periods/${periodId}/assignments/auto-assign-bawahan`,
    ASSIGNMENT: (assignmentId: string) => `/v1/penilaian/assignments/${assignmentId}`,
    DELETE_ASSIGNMENT: (assignmentId: string) =>
      `/v1/penilaian/assignments/${assignmentId}`,
    EMPLOYEE_OVERVIEW: (periodId: string) =>
      `/v1/penilaian/periods/${periodId}/employee-overview`,
    EMPLOYEE_KPIS: (periodId: string, employeeId: string) =>
      `/v1/penilaian/periods/${periodId}/employees/${employeeId}/kpi-assignments`,
    COMPUTE_SCORES: (periodId: string, employeeId: string) =>
      `/v1/penilaian/periods/${periodId}/employees/${employeeId}/compute-scores`,
    COMPUTE_ALL_SCORES: (periodId: string) =>
      `/v1/penilaian/periods/${periodId}/compute-all`,
    EMPLOYEE_SUBMITTED_ASSIGNMENTS: (periodId: string, employeeId: string) =>
      `/v1/penilaian/periods/${periodId}/employees/${employeeId}/submitted-assignments`,
    KPI_ASSIGNMENTS: '/v1/penilaian/kpi-assignments',
    KPI_REALISASI: (assignmentId: string) =>
      `/v1/penilaian/kpi-assignments/${assignmentId}/realisasi`,
    ASSIGNMENT_RESPONSES: (assignmentId: string) =>
      `/v1/penilaian/assignments/${assignmentId}/responses`,
    SUBMIT_ASSESSMENT: (assignmentId: string) =>
      `/v1/penilaian/assignments/${assignmentId}/submit`,
    MY_RESULT: '/v1/penilaian/my-result',
    MY_SUBMITTED_ASSESSMENTS: '/v1/penilaian/my-submitted-assessments',
    RESULTS: '/v1/penilaian/results',
    TEAM_RESULTS: '/v1/penilaian/team-results',
    RESULT: (id: string) => `/v1/penilaian/results/${id}`,
    APPROVE: (resultId: string, level: number) =>
      `/v1/penilaian/results/${resultId}/approve/${level}`,
    HR_REVIEW: (resultId: string) => `/v1/penilaian/results/${resultId}/review`,
    KPI_DEFINITION: (id: string) => `/v1/penilaian/kpi-definitions/${id}`,
    KPI_ASSIGNMENT: (id: string) => `/v1/penilaian/kpi-assignments/${id}`,
    KPI_PLANS_MY: '/v1/penilaian/kpi-plans/my',
    KPI_PLANS_MY_SUBMIT: '/v1/penilaian/kpi-plans/my/submit',
    KPI_PLANS_TEAM: '/v1/penilaian/kpi-plans/team',
    KPI_PLAN_APPROVE: (id: string) => `/v1/penilaian/kpi-plans/${id}/approve`,
    KPI_PLAN_REJECT: (id: string) => `/v1/penilaian/kpi-plans/${id}/reject`,
    EMPLOYEE_KPI_CREATE: '/v1/penilaian/employee-kpis',
    EMPLOYEE_KPIS_ANNUAL: (employeeId: string) =>
      `/v1/penilaian/employees/${employeeId}/kpi-assignments/annual`,
    COMPANY_KPIS: '/v1/penilaian/company-kpis',
    COMPANY_KPIS_SYNC: '/v1/penilaian/company-kpis/sync',
    COMPANY_KPI_REALISASI: '/v1/penilaian/company-kpis/realisasi',
  },
}
